import React, { useState, useEffect } from 'react';
import {
  Box,
  Link,
  Tab,
  Chip,
  CircularProgress,
  Typography,
  Avatar,
  TablePagination,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Alert
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import Images from '@/assets/Images';
import styles from './UserDetail.module.scss';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetUserByIdQuery,
  useGetUserRoleByIdQuery,
  useGetUserRolePermissionByIdQuery,
  useResendInvitationMutation,
} from '../../store/api/userApi';
import toast from '../../utils/toast';
import { formatDate as formatDateUtil } from '@/utils/formatters';
import { PermissionButton } from '@/components/common/PermissionButton';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';
import { parseApiError } from '@/utils/apiFieldErrors';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [tabValue, setValue] = useState('1');
  
  // Auto pagination navigation hook
  const { backToListing, toEdit } = useCompletePaginationNavigation(navigate, '/users');
  
  // State for role pagination
  const [rolePage, setRolePage] = useState(0);
  const [roleRowsPerPage, setRoleRowsPerPage] = useState(10);
  
  // State for permission pagination
  const [permissionPage, setPermissionPage] = useState(0);
  const [permissionRowsPerPage, setPermissionRowsPerPage] = useState(10);

  const {
    data: userDetailData,
    isLoading,
    isFetching,
    error: userError,
  } = useGetUserByIdQuery(userId || '', { skip: !userId });

  // Fetch paginated roles data from API - only when Roles tab is active
  const {
    data: rolesData,
    isLoading: isLoadingRoles,
    error: rolesError,
  } = useGetUserRoleByIdQuery(
    { userId: userId || '', page: rolePage + 1, limit: roleRowsPerPage },
    { skip: !userId || tabValue !== '1' }
  );

  // Fetch paginated permissions data from API - only when Permissions tab is active
  const {
    data: permissionsData,
    isLoading: isLoadingPermissions,
    error: permissionsError,
  } = useGetUserRolePermissionByIdQuery(
    { userId: userId || '', page: permissionPage + 1, limit: permissionRowsPerPage },
    { skip: !userId || tabValue !== '2' }
  );

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const handlePermissionRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPermissionRowsPerPage(parseInt(event.target.value, 10));
    setPermissionPage(0);
  };

  const handleRoleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoleRowsPerPage(parseInt(event.target.value, 10));
    setRolePage(0);
  };

  const handleRolePageChange = (_event: unknown, newPage: number) => {
    setRolePage(newPage);
  };

  const handlePermissionPageChange = (_event: unknown, newPage: number) => {
    setPermissionPage(newPage);
  };

  const handleEdit = () => {
    if (userId) {
      toEdit(userId);
    }
  };

  const [resendInvitation, { isLoading: isResending }] = useResendInvitationMutation();

  const canResend =
    Boolean(userDetailData?.invitation_sent_at) && !userDetailData?.invitation_accepted_at;

  const handleResendInvitation = async () => {
    if (!userId) return;
    try {
      await resendInvitation(userId).unwrap();
      toast.success('Invitation resent successfully');
    } catch (err) {
      if (typeof err === 'object' && err !== null && (err as { status?: number }).status === 429) {
        toast.error('Too many invitation requests. Please try again later.');
      } else {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to resend invitation');
      }
    }
  };

  useEffect(() => {
    if (userError) {
      toast.error('Failed to load user details');
      backToListing();
    }
  }, [userError, backToListing]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '--';
    return formatDateUtil(dateString, 'MMM d, yyyy h:mm a', dateString);
  };

  const getReportingManagerName = () => {
    if (!userDetailData?.reporting_manager_details) return '--';
    return userDetailData.reporting_manager_details.name || '--';
  };

  if (isLoading || isFetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userDetailData) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">
          <Link onClick={backToListing} className="back">
            <img src={Images.BACK} alt="Back" width="22" />
          </Link>
          Error
        </h3>
        <div>Failed to load user details</div>
      </div>
    );
  }

  return (
    <div className="appWrapper">
      <h3 className="pageTitle">
        <Link onClick={backToListing} className="back">
          <img src={Images.BACK} alt="Back" width="22" />
        </Link>
        User Details
      </h3>
      <div className="appGridContainer">
        <div className="gridHeader">
          <Box display="flex" justifyContent="flex-end" gap={1} width="100%">
            {canResend && (
              <PermissionButton
                module="user"
                permissionAction="edit"
                variant="outlined"
                color="primary"
                onClick={handleResendInvitation}
                disabled={isResending}
              >
                {isResending ? 'Resending...' : 'Resend Invitation'}
              </PermissionButton>
            )}
            <PermissionButton
              module="user"
              permissionAction="edit"
              variant="contained"
              color="primary"
              onClick={handleEdit}
              className="button button__solid--primary"
            >
              Edit
            </PermissionButton>
          </Box>
        </div>
        <div className="gridWrapper">
          <div className={`${styles.userDetWrapper}`}>
            <div className={`${styles.userDetContainer} bordered r10`}>
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar
                  sx={{ width: 80, height: 80, mr: 3 }}
                  alt={`${userDetailData.first_name} ${userDetailData.last_name}`}
                  src={userDetailData.additional_info?.profile_picture || undefined}
                />
                <Box>
                  <Typography variant="h4">
                    {userDetailData.first_name} {userDetailData.last_name}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {userDetailData.email}
                  </Typography>
                </Box>
              </Box>

              <section className={`border_bottom ${styles.userBasic}`}>
                <div className="d_flex">
                  <div className="col">
                    <label className="label">Status</label>
                    <h5 className="body1">
                      <Chip
                        label={userDetailData.is_active ? 'Active' : 'Inactive'}
                        color={userDetailData.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </h5>
                  </div>
                  <div className="col">
                    <label className="label">Employee ID</label>
                    <h5 className="body1">{userDetailData.employee_id || '--'}</h5>
                  </div>
                  <div className="col">
                    <label className="label">Contact Number</label>
                    <h5 className="body1">{userDetailData.phone_number || '--'}</h5>
                  </div>
                </div>
              </section>

              <section className={`border_bottom ${styles.userBasic}`}>
                <div className="d_flex">
                  <div className="col">
                    <label className="label">Position</label>
                    <h5 className="body1">{userDetailData.position || '--'}</h5>
                  </div>
                  <div className="col">
                    <label className="label">Reporting Manager</label>
                    <h5 className="body1">{getReportingManagerName()}</h5>
                  </div>
                </div>
              </section>

              <section className="border_bottom pb_30 pt_30">
                <div className="d_flex wrap">
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Invitation Sent</label>
                    <h5 className="body1">
                      {formatDate(userDetailData.invitation_sent_at)}
                    </h5>
                  </div>
                  <div className="col_33 mt_10 mb_10">
                    <label className="label">Invitation Accepted</label>
                    <h5 className="body1">
                      {formatDate(userDetailData.invitation_accepted_at)}
                    </h5>
                  </div>
                </div>
              </section>
            </div>

            <section className="tab_container mt_30">
              <TabContext value={tabValue}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TabList onChange={handleChange} aria-label="user tabs">
                    <Tab label="Roles" value="1" />
                    <Tab label="Permissions" value="2" />
                  </TabList>
                </Box>

                {/* Roles Tab */}
                <TabPanel value="1">
                  {rolesError ? (
                    <Alert severity="error">Failed to load roles. Please try again.</Alert>
                  ) : isLoadingRoles ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <CircularProgress />
                    </Box>
                  ) : rolesData?.results?.length > 0 ? (
                    <Box>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Role Name</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Type</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rolesData.results.map((role) => (
                              <TableRow key={role.id}>
                                <TableCell>
                                  <Chip
                                    label={role.name}
                                    color={
                                      role.name.toLowerCase() === 'superadmin'
                                        ? 'secondary'
                                        : role.name.toLowerCase() === 'admin'
                                        ? 'primary'
                                        : 'default'
                                    }
                                  />
                                </TableCell>
                                <TableCell>{role.description || '-'}</TableCell>
                                <TableCell>
                                  {role.is_system_role ? 'System Role' : 'Custom Role'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={rolesData.count || 0}
                        rowsPerPage={roleRowsPerPage}
                        page={rolePage}
                        onPageChange={handleRolePageChange}
                        onRowsPerPageChange={handleRoleRowsPerPageChange}
                      />
                    </Box>
                  ) : (
                    <div className="empty_state">
                      <p>No roles assigned to this user</p>
                    </div>
                  )}
                </TabPanel>

                {/* Permissions Tab */}
                <TabPanel value="2">
                  {permissionsError ? (
                    <Alert severity="error">Failed to load permissions. Please try again.</Alert>
                  ) : isLoadingPermissions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <CircularProgress />
                    </Box>
                  ) : permissionsData?.results?.length > 0 ? (
                    <Box>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Permission Name</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell>Role</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {permissionsData.results.map((permission) => (
                              <TableRow key={permission.permission_id}>
                                <TableCell>{permission.permission_name}</TableCell>
                                <TableCell>{permission.permission_description || '-'}</TableCell>
                                <TableCell>{permission.role_name}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={permissionsData.count || 0}
                        rowsPerPage={permissionRowsPerPage}
                        page={permissionPage}
                        onPageChange={handlePermissionPageChange}
                        onRowsPerPageChange={handlePermissionRowsPerPageChange}
                      />
                    </Box>
                  ) : (
                    <div className="empty_state">
                      <p>No permissions assigned to this user</p>
                    </div>
                  )}
                </TabPanel>
              </TabContext>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetail;